import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { AuthService } from '../../../services/auth/auth';
import { UserService, UserProfile } from '../../../services/api/user';
import { LanguageService } from '../../../services/i18n/language';
import { RequisitesService, CompanyRequisites, BankAccount } from '../../../services/api/requisites';
import { ViewRequisitesComponent } from '../../shared/view-requisites/view-requisites.component';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, ViewRequisitesComponent],
  templateUrl: './profile.html',
  styleUrl: './profile.scss'
})
export class ProfileComponent implements OnInit {
  user: UserProfile | null = null;
  loading = false;
  error: string | null = null;

  // Вкладки
  activeTab: 'profile' | 'legal' = 'profile';

  // Реквизиты
  requisites: CompanyRequisites | null = null;
  requisitesForm: FormGroup;
  loadingRequisites = false;
  savingRequisites = false;
  requisitesError: string | null = null;
  requisitesSuccess = false;
  isEditingRequisites = false; // Режим редактирования

  // Тексты для i18n
  titleText = 'Профиль';
  loadingText = 'Загрузка...';
  profileTabText = 'Профиль';
  legalDetailsTabText = 'Реквизиты';
  personalInfoText = 'Личная информация';
  companyInfoText = 'Информация о компании';
  fullNameText = 'ФИО';
  emailText = 'Email';
  nameText = 'Имя';
  phoneText = 'Телефон';
  roleText = 'Роль';
  companyNameText = 'Название компании';
  countryText = 'Страна';
  industryText = 'Отрасль';
  commentText = 'Комментарий';
  createdAtText = 'Дата регистрации';
  logoutTitle = 'Выйти';
  emptyText = 'Данные профиля недоступны';

  // Удаление аккаунта
  showDeleteModal = false;
  deleteConfirmText = '';
  deletingAccount = false;
  deleteError: string | null = null;
  deleteAccountTitle = 'Удаление аккаунта';
  deleteAccountWarning = 'Это действие необратимо. Ваш профиль будет удалён, а личные данные (имя, email, телефон) обезличены. Активные заявки и сделки сохранятся для истории ваших контрагентов.';
  deleteAccountConfirmLabel = 'Введите УДАЛИТЬ, чтобы подтвердить';
  deleteAccountButtonText = 'Удалить аккаунт';
  deleteAccountConfirmButtonText = 'Удалить навсегда';
  deleteAccountCancelText = 'Отмена';
  deleteAccountConfirmWord = 'УДАЛИТЬ';
  
  // Реквизиты тексты
  basicInfoText = 'Основная информация';
  bankingInfoText = 'Банковская информация';
  binText = 'ИИН/БИН';
  legalAddressText = 'Юридический адрес';
  bankNameText = 'Банк';
  bikText = 'БИК';
  bankAccountsText = 'Счета в банке';
  currencyText = 'Валюта';
  accountNumberText = 'Номер счета';
  addAccountText = 'Добавить счет';
  removeAccountText = 'Удалить';
  contractSignerText = 'Подписант договора';
  signerPositionText = 'Должность';
  signerNameText = 'ФИО';
  saveText = 'Сохранить';
  savingText = 'Сохранение...';
  successText = 'Реквизиты успешно сохранены';
  editText = 'Редактировать';
  cancelText = 'Отменить';
  noRequisitesText = 'Реквизиты не заполнены';
  fillRequisitesText = 'Заполнить реквизиты';
  selectCurrencyText = 'Выберите валюту';

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private requisitesService: RequisitesService,
    private router: Router,
    private languageService: LanguageService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.requisitesForm = this.fb.group({
      bin: [''],
      legalAddress: [''],
      bankName: [''],
      bik: [''],
      bankAccounts: this.fb.array([]),
      contractSignerPosition: [''],
      contractSignerName: ['']
    });
  }

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login']);
      return;
    }

    this.loadProfile();
    this.loadRequisites();
    this.updateTranslations();

    this.languageService.getTranslations().subscribe(() => {
      this.updateTranslations();
      this.cdr.detectChanges();
    });
  }

  switchTab(tab: 'profile' | 'legal'): void {
    this.activeTab = tab;
    if (tab === 'legal' && !this.requisites) {
      this.loadRequisites();
    }
    // Сбросить режим редактирования при переключении вкладок
    this.isEditingRequisites = false;
  }

  startEditingRequisites(): void {
    this.isEditingRequisites = true;
    this.requisitesError = null;
    this.requisitesSuccess = false;
  }

  cancelEditingRequisites(): void {
    this.isEditingRequisites = false;
    this.requisitesError = null;
    this.requisitesSuccess = false;
    // Восстановить данные из текущих реквизитов
    if (this.requisites) {
      this.populateRequisitesForm(this.requisites);
    } else {
      this.initEmptyForm();
    }
  }

  get bankAccounts(): FormArray {
    return this.requisitesForm.get('bankAccounts') as FormArray;
  }

  addBankAccount(): void {
    const accountGroup = this.fb.group({
      currency: [''],
      account: ['']
    });
    this.bankAccounts.push(accountGroup);
  }

  removeBankAccount(index: number): void {
    this.bankAccounts.removeAt(index);
  }

  private loadRequisites(): void {
    this.loadingRequisites = true;
    this.requisitesError = null;
    this.isEditingRequisites = false; // Убедиться, что режим просмотра по умолчанию

    this.requisitesService.getMyRequisites().subscribe({
      next: (requisites) => {
        // Пустой ответ — то же, что «реквизиты не заполнены» (как 404)
        this.requisites = requisites || null;
        if (requisites) {
          this.populateRequisitesForm(requisites);
        } else {
          this.initEmptyForm();
        }
        this.loadingRequisites = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        // Если реквизиты не найдены (404), это нормально
        if (err.status === 404) {
          this.requisites = null;
          this.initEmptyForm();
        } else {
          console.error('Error loading requisites:', err);
          this.requisitesError = 'Ошибка при загрузке реквизитов';
        }
        this.loadingRequisites = false;
        this.cdr.markForCheck();
      }
    });
  }

  private populateRequisitesForm(requisites: CompanyRequisites): void {
    this.requisitesForm.patchValue({
      bin: requisites.bin || '',
      legalAddress: requisites.legalAddress || '',
      bankName: requisites.bankName || '',
      bik: requisites.bik || '',
      contractSignerPosition: requisites.contractSignerPosition || '',
      contractSignerName: requisites.contractSignerName || ''
    });

    // Очистить и добавить счета
    this.bankAccounts.clear();
    if (requisites.bankAccounts && requisites.bankAccounts.length > 0) {
      requisites.bankAccounts.forEach(account => {
        this.bankAccounts.push(this.fb.group({
          currency: [account.currency || ''],
          account: [account.account || '']
        }));
      });
    } else {
      this.addBankAccount();
    }
  }

  private initEmptyForm(): void {
    this.requisitesForm.reset();
    this.bankAccounts.clear();
    this.addBankAccount();
  }

  saveRequisites(): void {
    this.savingRequisites = true;
    this.requisitesError = null;
    this.requisitesSuccess = false;

    const formValue = this.requisitesForm.value;
    const request: CompanyRequisites = {
      bin: formValue.bin || undefined,
      legalAddress: formValue.legalAddress || undefined,
      bankName: formValue.bankName || undefined,
      bik: formValue.bik || undefined,
      bankAccounts: formValue.bankAccounts?.filter((acc: BankAccount) => acc.currency && acc.account) || [],
      contractSignerPosition: formValue.contractSignerPosition || undefined,
      contractSignerName: formValue.contractSignerName || undefined
    };

    this.requisitesService.saveMyRequisites(request).subscribe({
      next: (requisites) => {
        this.requisites = requisites;
        this.requisitesSuccess = true;
        this.savingRequisites = false;
        this.isEditingRequisites = false; // Переключить в режим просмотра
        this.cdr.markForCheck();
        
        setTimeout(() => {
          this.requisitesSuccess = false;
          this.cdr.markForCheck();
        }, 3000);
      },
      error: (err) => {
        console.error('Error saving requisites:', err);
        this.requisitesError = err.error?.message || 'Ошибка при сохранении реквизитов';
        this.savingRequisites = false;
        this.cdr.markForCheck();
      }
    });
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('profile.title') || 'Профиль';
    this.loadingText = this.languageService.translate('profile.loading') || 'Загрузка...';
    this.profileTabText = this.languageService.translate('profile.tabs.profile') || 'Профиль';
    this.legalDetailsTabText = this.languageService.translate('profile.tabs.legalDetails') || 'Реквизиты';
    this.personalInfoText = this.languageService.translate('profile.personalInfo') || 'Личная информация';
    this.companyInfoText = this.languageService.translate('profile.companyInfo') || 'Информация о компании';
    this.fullNameText = this.languageService.translate('profile.fullName') || 'ФИО';
    this.emailText = this.languageService.translate('profile.email.label') || 'Email';
    const name = this.languageService.translate('phoneClaim.nameLabel');
    this.nameText = name && name !== 'phoneClaim.nameLabel' ? name : 'Имя';
    this.phoneText = this.languageService.translate('profile.phone.label') || 'Телефон';
    this.roleText = this.languageService.translate('profile.role') || 'Роль';
    this.companyNameText = this.languageService.translate('profile.companyName') || 'Название компании';
    this.countryText = this.languageService.translate('profile.country') || 'Страна';
    this.industryText = this.languageService.translate('profile.industry') || 'Отрасль';
    this.commentText = this.languageService.translate('profile.comment') || 'Комментарий';
    this.createdAtText = this.languageService.translate('profile.createdAt') || 'Дата регистрации';
    this.logoutTitle = this.languageService.translate('profile.logout.title') || 'Выйти';
    this.emptyText = this.languageService.translate('profile.empty') || 'Данные профиля недоступны';
    
    this.basicInfoText = this.languageService.translate('profile.legalDetails.basicInfo') || 'Основная информация';
    this.bankingInfoText = this.languageService.translate('profile.legalDetails.bankingInfo') || 'Банковская информация';
    this.binText = this.languageService.translate('profile.legalDetails.bin') || 'ИИН/БИН';
    this.legalAddressText = this.languageService.translate('profile.legalDetails.legalAddress') || 'Юридический адрес';
    this.bankNameText = this.languageService.translate('profile.legalDetails.bankName') || 'Банк';
    this.bikText = this.languageService.translate('profile.legalDetails.bik') || 'БИК';
    this.bankAccountsText = this.languageService.translate('profile.legalDetails.bankAccounts') || 'Счета в банке';
    this.currencyText = this.languageService.translate('profile.legalDetails.currency') || 'Валюта';
    this.accountNumberText = this.languageService.translate('profile.legalDetails.accountNumber') || 'Номер счета';
    this.addAccountText = this.languageService.translate('profile.legalDetails.addAccount') || 'Добавить счет';
    this.removeAccountText = this.languageService.translate('profile.legalDetails.removeAccount') || 'Удалить';
    this.contractSignerText = this.languageService.translate('profile.legalDetails.contractSigner') || 'Подписант договора';
    this.signerPositionText = this.languageService.translate('profile.legalDetails.signerPosition') || 'Должность';
    this.signerNameText = this.languageService.translate('profile.legalDetails.signerName') || 'ФИО';
    this.saveText = this.languageService.translate('profile.legalDetails.save') || 'Сохранить';
    this.savingText = this.languageService.translate('profile.legalDetails.saving') || 'Сохранение...';
    this.successText = this.languageService.translate('profile.legalDetails.success') || 'Реквизиты успешно сохранены';
    this.editText = this.languageService.translate('profile.legalDetails.edit') || 'Редактировать';
    this.cancelText = this.languageService.translate('profile.legalDetails.cancel') || 'Отменить';
    this.noRequisitesText = this.languageService.translate('profile.legalDetails.noRequisites') || 'Реквизиты не заполнены';
    this.fillRequisitesText = this.languageService.translate('profile.legalDetails.fillRequisites') || 'Заполнить реквизиты';
    this.selectCurrencyText = this.languageService.translate('profile.legalDetails.selectCurrency') || this.selectCurrencyText;

    this.deleteAccountTitle = this.languageService.translate('profile.deleteAccount.title') || this.deleteAccountTitle;
    this.deleteAccountWarning = this.languageService.translate('profile.deleteAccount.warning') || this.deleteAccountWarning;
    this.deleteAccountConfirmLabel = this.languageService.translate('profile.deleteAccount.confirmLabel') || this.deleteAccountConfirmLabel;
    this.deleteAccountButtonText = this.languageService.translate('profile.deleteAccount.button') || this.deleteAccountButtonText;
    this.deleteAccountConfirmButtonText = this.languageService.translate('profile.deleteAccount.confirmButton') || this.deleteAccountConfirmButtonText;
    this.deleteAccountCancelText = this.languageService.translate('profile.deleteAccount.cancel') || this.deleteAccountCancelText;
    this.deleteAccountConfirmWord = this.languageService.translate('profile.deleteAccount.confirmWord') || this.deleteAccountConfirmWord;
  }

  openDeleteModal(): void {
    this.showDeleteModal = true;
    this.deleteConfirmText = '';
    this.deleteError = null;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.deleteConfirmText = '';
    this.deleteError = null;
  }

  get canConfirmDelete(): boolean {
    return this.deleteConfirmText.trim().toUpperCase() === this.deleteAccountConfirmWord.toUpperCase();
  }

  confirmDeleteAccount(): void {
    if (!this.canConfirmDelete || this.deletingAccount) {
      return;
    }
    this.deletingAccount = true;
    this.deleteError = null;

    this.userService.deleteCurrentUser().subscribe({
      next: () => {
        this.deletingAccount = false;
        this.showDeleteModal = false;
        this.authService.logout();
        this.router.navigate(['/']);
      },
      error: (err) => {
        console.error('Error deleting account:', err);
        this.deleteError = err.error?.message || 'Ошибка при удалении аккаунта';
        this.deletingAccount = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadProfile(): void {
    this.loading = true;
    this.error = null;

    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        this.user = user;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Profile: Error loading profile', err);
        this.error = 'Ошибка при загрузке профиля';
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  get initials(): string {
    return (this.user?.fullName || this.user?.email || '?').trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('');
  }

  /** Клиент, вошедший по телефону. */
  get isPublicClient(): boolean {
    return this.user?.role?.code === 'ROLE_PUBLIC';
  }

  /**
   * Имя для показа. Если имени нет, бэкенд кладёт в fullName телефон (а у гостя — «Гость») —
   * такое за имя не выдаём.
   */
  get displayName(): string {
    const name = (this.user?.fullName ?? '').trim();
    const digits = name.replace(/\D/g, '');
    const looksLikePhone = digits.length >= 10 && digits.length >= name.replace(/\s/g, '').length - 2;
    return !name || looksLikePhone || name === 'Гость' ? '' : name;
  }

  getRoleName(): string {
    if (!this.user?.role) return '-';
    return this.user.role.name;
  }

  getCountryName(): string {
    if (!this.user?.country) return '-';
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? this.user.country.nameEn : this.user.country.nameRu;
  }

  getIndustryName(): string {
    if (!this.user?.industry) return '-';
    const lang = this.languageService.getCurrentLanguageValue();
    return lang === 'en' ? this.user.industry.nameEn : this.user.industry.nameRu;
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const lang = this.languageService.getCurrentLanguageValue();
    return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }
}

